import React, { useState, useEffect, useCallback } from 'react';
import { ClipboardList, Video, Loader2, Check, Minus, Plus, Scan, Dumbbell, RotateCcw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FIELDS = [
  ['lieu', "Lieu d'entraînement"], ['materiel', 'Matériel'], ['disponibilites', 'Disponibilités'],
  ['blessures', 'Blessures / limitations'], ['alimentation', 'Préférences alimentaires'], ['motivation', 'Motivation & échéance'],
];

const GOAL_LABELS = { weight_loss: 'Perte de poids', muscle_gain: 'Prise de muscle', recomposition: 'Recomposition', maintenance: 'Maintien' };
const LEVEL_LABELS = { beginner: 'Débutant', intermediate: 'Intermédiaire', advanced: 'Avancé' };
const GENDER_LABELS = { male: 'Homme', female: 'Femme' };
const PROFILE_FIELDS = [
  ['gender', 'Genre', GENDER_LABELS], ['age', 'Âge', null, ' ans'], ['weight', 'Poids', null, ' kg'],
  ['height', 'Taille', null, ' cm'], ['goal', 'Objectif', GOAL_LABELS], ['training_level', 'Niveau', LEVEL_LABELS],
];

export const CoachCoaching = ({ email, csrfToken }) => {
  const fetchWithCredentials = (url) => fetch(url, { credentials: 'include' });
  const fetchWithCsrf = (url, options = {}) => {
    return fetch(url, {
      ...options,
      credentials: 'include',
      headers: {
        ...options.headers,
        'X-CSRF-Token': csrfToken,
      },
    });
  };
  const jsonHeaders = { 'Content-Type': 'application/json' };
  
  const [ob, setOb] = useState(null);
  const [profile, setProfile] = useState(null);
  const [visio, setVisio] = useState({ zoom_url: '', scheduled_at: '', note: '' });
  const [plan, setPlan] = useState(null);
  const [credits, setCredits] = useState(null);
  const [progReq, setProgReq] = useState(null);
  const [progReqBusy, setProgReqBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  const load = useCallback(() => {
    fetchWithCredentials(`${BACKEND_URL}/api/admin/clients/${encodeURIComponent(email)}/onboarding`)
      .then((r) => r.json())
      .then((d) => {
        setOb(d.onboarding);
        setProfile(d.profile || null);
        setPlan(d.plan || null);
        setCredits(d.credits || null);
        if (d.visio) setVisio({ zoom_url: d.visio.zoom_url || '', scheduled_at: (d.visio.scheduled_at || '').slice(0, 16), note: d.visio.note || '' });
      })
      .catch(() => {});
    fetchWithCredentials(`${BACKEND_URL}/api/admin/clients/${encodeURIComponent(email)}/program-requests`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setProgReq(d))
      .catch(() => {});
  }, [email]);
  useEffect(() => { load(); }, [load]);

  const adjustCredit = (patchField, displayKey, delta) => {
    const current = credits?.[displayKey] || 0;
    const next = Math.max(0, current + delta);
    fetchWithCsrf(`${BACKEND_URL}/api/admin/clients/${encodeURIComponent(email)}/credits`, {
      method: 'PATCH', headers: jsonHeaders, body: JSON.stringify({ [patchField]: next }),
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then(() => setCredits((c) => ({ ...c, [displayKey]: next })))
      .catch(() => window.alert("Échec de l'ajustement du crédit."));
  };

  const resetLastProgramRequest = () => {
    setProgReqBusy(true);
    fetchWithCsrf(`${BACKEND_URL}/api/admin/clients/${encodeURIComponent(email)}/program-requests/reset-last`, { method: 'POST' })
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((d) => setProgReq(d))
      .catch(() => window.alert("Aucune demande de programme à annuler pour ce client."))
      .finally(() => setProgReqBusy(false));
  };

  const saveVisio = () => {
    setBusy(true); setSaved(false);
    fetchWithCsrf(`${BACKEND_URL}/api/admin/clients/${encodeURIComponent(email)}/visio`, { 
      method: 'PUT', 
      headers: jsonHeaders, 
      body: JSON.stringify(visio) 
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then(() => { setSaved(true); setTimeout(() => setSaved(false), 2000); })
      .catch(() => window.alert("Échec de l'enregistrement."))
      .finally(() => setBusy(false));
  };

  return (
    <div className="bg-zinc-900 border border-white/10 rounded-2xl p-4 mt-5" data-testid="coach-coaching">
      {/* Crédits visio / séance / scan 3D */}
      {credits && (
        <div className="mb-5" data-testid="admin-credits-editor">
          <h3 className="flex items-center gap-2 font-bold text-white text-sm mb-3"><Scan className="w-4 h-4 text-emerald-400" /> Crédits ({plan === 'vip' ? 'VIP Hybrid' : 'Transformation'})</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="rounded-lg bg-zinc-800/50 border border-white/10 p-3 flex items-center justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-zinc-500">{credits.type === 'studio_or_zoom' ? 'Séances studio/Zoom' : 'Visios Zoom'}</p>
                <p className="text-white font-bold font-mono" data-testid="admin-credits-zoom-remaining">{credits.total - credits.used}/{credits.total} restantes</p>
              </div>
              <div className="flex items-center gap-1.5">
                <button onClick={() => adjustCredit(credits.type === 'studio_or_zoom' ? 'studio_credits_used' : 'zoom_credits_used', 'used', -1)} className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white" data-testid="credit-zoom-refund-btn"><Plus className="w-3.5 h-3.5" /></button>
                <button onClick={() => adjustCredit(credits.type === 'studio_or_zoom' ? 'studio_credits_used' : 'zoom_credits_used', 'used', 1)} className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white" data-testid="credit-zoom-use-btn"><Minus className="w-3.5 h-3.5" /></button>
              </div>
            </div>
            {credits.type === 'studio_or_zoom' && (
              <div className="rounded-lg bg-zinc-800/50 border border-white/10 p-3 flex items-center justify-between">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-zinc-500">Scans 3D</p>
                  <p className="text-white font-bold font-mono" data-testid="admin-credits-scan3d-remaining">{credits.scan3d_total - credits.scan3d_used}/{credits.scan3d_total} restants</p>
                </div>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => adjustCredit('scan3d_used', 'scan3d_used', -1)} className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white" data-testid="credit-scan3d-refund-btn"><Plus className="w-3.5 h-3.5" /></button>
                  <button onClick={() => adjustCredit('scan3d_used', 'scan3d_used', 1)} className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white" data-testid="credit-scan3d-use-btn"><Minus className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Crédit "programme personnalisé" (1/mois inclus, tous packs coaching) */}
      {progReq && !progReq.locked && (
        <div className="mb-5" data-testid="admin-program-requests-editor">
          <h3 className="flex items-center gap-2 font-bold text-white text-sm mb-3"><Dumbbell className="w-4 h-4 text-blue-400" /> Crédit programme personnalisé</h3>
          <div className="rounded-lg bg-zinc-800/50 border border-white/10 p-3 flex items-center justify-between gap-3 flex-wrap">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-zinc-500">Crédit ce mois-ci</p>
              <p className="text-white font-bold font-mono" data-testid="admin-program-requests-count">{progReq.coach_request_remaining}/1</p>
              {progReq.reason === 'pending' && <p className="text-[11px] text-blue-400 mt-0.5">Une demande est en attente de création.</p>}
              {progReq.can_request && <p className="text-[11px] text-emerald-400 mt-0.5">Le client peut faire une demande ce mois-ci.</p>}
            </div>
            <button
              onClick={resetLastProgramRequest}
              disabled={progReqBusy}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-white/10 hover:bg-white/20 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              data-testid="program-request-reset-btn"
            >
              {progReqBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />} Annuler la dernière demande (+1 crédit)
            </button>
          </div>

          {progReq.pending_request && (
            <div className="mt-3 rounded-lg bg-blue-500/10 border border-blue-500/25 p-3" data-testid="admin-pending-request-details">
              <p className="text-xs font-bold text-blue-300 uppercase tracking-wide mb-2">Demande en attente — à créer dans Programmes Pro (délai 48h)</p>
              {progReq.pending_request.carte_blanche ? (
                <p className="text-sm text-white">Carte blanche : le client laisse le coach décider entièrement.</p>
              ) : (
                <div className="space-y-1.5 text-sm">
                  {progReq.pending_request.satisfaction && <p className="text-zinc-300">Satisfaction actuelle : <span className="text-white font-semibold">{progReq.pending_request.satisfaction}/5</span></p>}
                  {progReq.pending_request.liked && <p className="text-zinc-300">Aimé : <span className="text-white">{progReq.pending_request.liked}</span></p>}
                  {progReq.pending_request.disliked && <p className="text-zinc-300">Pas aimé : <span className="text-white">{progReq.pending_request.disliked}</span></p>}
                  {progReq.pending_request.preferences && <p className="text-zinc-300">Préférences : <span className="text-white">{progReq.pending_request.preferences}</span></p>}
                </div>
              )}
              <p className="text-[11px] text-zinc-500 mt-2">Créez le programme dans Programmes Pro ci-dessus — le crédit sera décompté (ou offert, à votre choix) à ce moment-là.</p>
            </div>
          )}
        </div>
      )}

      {/* Visio */}
      <h3 className="flex items-center gap-2 font-bold text-white text-sm mb-3"><Video className="w-4 h-4 text-purple-400" /> Visio mensuelle</h3>
      <div className="grid sm:grid-cols-3 gap-2 mb-2">
        <input value={visio.zoom_url} onChange={(e) => setVisio((v) => ({ ...v, zoom_url: e.target.value }))} placeholder="Lien Zoom / Meet" className="bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-primary focus:outline-none" data-testid="visio-url-input" />
        <input type="datetime-local" value={visio.scheduled_at} onChange={(e) => setVisio((v) => ({ ...v, scheduled_at: e.target.value }))} className="bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-primary focus:outline-none" data-testid="visio-date-input" />
        <input value={visio.note} onChange={(e) => setVisio((v) => ({ ...v, note: e.target.value }))} placeholder="Note (sujet…)" className="bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-primary focus:outline-none" />
      </div>
      <button onClick={saveVisio} disabled={busy} className="px-4 py-2 rounded-lg font-bold text-white bg-gradient-to-r from-purple-500 to-primary disabled:opacity-50 inline-flex items-center gap-2 text-sm" data-testid="save-visio-btn">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : saved ? <Check className="w-4 h-4" /> : null} {saved ? 'Enregistré' : 'Enregistrer la visio'}
      </button>

      {/* Onboarding */}
      <h3 className="flex items-center gap-2 font-bold text-white text-sm mt-5 mb-3"><ClipboardList className="w-4 h-4 text-primary" /> Onboarding du client</h3>
      {profile && profile.has_profile !== undefined && PROFILE_FIELDS.some(([k]) => profile[k] !== undefined && profile[k] !== null && profile[k] !== '') && (
        <div className="mb-4" data-testid="coach-profile-summary">
          <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Profil (âge/poids/taille/objectif)</p>
          <div className="grid sm:grid-cols-3 gap-2">
            {PROFILE_FIELDS.map(([k, lab, dict, suffix]) => {
              const raw = profile[k];
              if (raw === undefined || raw === null || raw === '') return null;
              const display = dict ? (dict[raw] || raw) : `${raw}${suffix || ''}`;
              return (
                <div key={k} className="rounded-lg bg-zinc-800/50 border border-white/10 p-2.5">
                  <p className="text-[10px] uppercase tracking-wider text-zinc-500">{lab}</p>
                  <p className="text-sm text-white mt-0.5">{display}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {ob?.completed_at ? (
        <>
          {ob.updated_at && ob.updated_at !== ob.completed_at && (
            <p className="text-xs text-amber-400 mb-2" data-testid="onboarding-updated-badge">✏️ Réponses mises à jour le {new Date(ob.updated_at).toLocaleString('fr-CH', { dateStyle: 'short', timeStyle: 'short' })}</p>
          )}
          <div className="grid sm:grid-cols-2 gap-2" data-testid="coach-onboarding-answers">
            {FIELDS.map(([k, lab]) => (
              <div key={k} className="rounded-lg bg-zinc-800/50 border border-white/10 p-2.5">
                <p className="text-[10px] uppercase tracking-wider text-zinc-500">{lab}</p>
                <p className="text-sm text-white mt-0.5 whitespace-pre-wrap">{ob.answers?.[k] || '—'}</p>
              </div>
            ))}
          </div>
        </>
      ) : (
        <p className="text-zinc-500 text-sm">Le client n'a pas encore rempli son questionnaire d'onboarding.</p>
      )}
    </div>
  );
};

export default CoachCoaching;
